import { getDefaultStore } from "jotai";

import { lastEventIdAtom } from "@/atoms/tonConnect";

import { SessionCrypto } from "./SessionService";
import {
  type AccountConnection,
  type AppRequest,
  type ConnectEvent,
  type DisconnectEvent,
  type KeyPair,
  type RpcMethod,
  type TonConnectAppRequest,
  type TonConnectMessageRequest,
  type WalletResponse,
} from "./types";

export const defaultTtl = 300;

export const BRIDGE_API_FQDN = "https://tc.architecton.su/bridge";

export const sendEventToBridge = async <T extends RpcMethod>({
  response,
  sessionKeyPair,
  clientSessionId,
  ttl = defaultTtl,
}: {
  response: WalletResponse<T> | ConnectEvent | DisconnectEvent;
  sessionKeyPair: KeyPair;
  clientSessionId: string;
  ttl?: number;
}) => {
  const sessionCrypto = new SessionCrypto(sessionKeyPair);

  const encodedResponse = sessionCrypto.encrypt(JSON.stringify(response), Buffer.from(clientSessionId, "hex"));

  const url = `${BRIDGE_API_FQDN}/message?client_id=${sessionCrypto.sessionId}&to=${clientSessionId}&ttl=${ttl}`;

  await fetch(url, {
    body: Buffer.from(encodedResponse).toString("base64"),
    method: "POST",
  });
};

const setLastEventId = async (lastEventId: string) => {
  const store = getDefaultStore();
  store.set(lastEventIdAtom, lastEventId);
};

interface TonConnectRequest {
  from: string;
  message: string;
}

export const decryptTonConnectMessage = ({
  message,
  from,
  connection,
}: TonConnectMessageRequest): TonConnectAppRequest => {
  const sessionCrypto = new SessionCrypto(connection.sessionKeyPair);

  const request: AppRequest<RpcMethod> = JSON.parse(
    sessionCrypto.decrypt(Buffer.from(message, "base64"), Buffer.from(from, "hex")),
  );

  return { request, connection };
};

export const subscribeTonConnect = ({
  handleMessage,
  connections,
  lastEventId,
}: {
  handleMessage: (params: TonConnectAppRequest) => void;
  connections?: AccountConnection[];
  lastEventId?: string;
}) => {
  if (!connections || connections.length === 0) {
    return () => {};
  }

  const walletSessionIds = connections.map((item) => new SessionCrypto(item.sessionKeyPair).sessionId).join(",");

  let url = `/events?client_id=${walletSessionIds}`;

  if (lastEventId) {
    url += `&last_event_id=${lastEventId}`;
  }

  console.debug("sse connect", url);

  const eventSource = new EventSource(`${BRIDGE_API_FQDN}${url}`);

  const onMessage = (params: MessageEvent<string>) => {
    setLastEventId(params.lastEventId);

    const { from, message }: TonConnectRequest = JSON.parse(params.data);

    const connection = connections.find((item) => item.clientSessionId === from);
    if (!connection) return;

    const decryptedMessage = decryptTonConnectMessage({ message, from, connection });

    handleMessage(decryptedMessage);
  };

  const onOpen = () => {
    console.log("sse connect: opened");
  };

  const onError = (event: Event) => {
    console.log("sse connect: error", event);
  };

  eventSource.addEventListener("message", onMessage);
  eventSource.addEventListener("open", onOpen);
  eventSource.addEventListener("error", onError);

  return () => {
    eventSource.removeEventListener("message", onMessage);
    eventSource.removeEventListener("open", onOpen);
    eventSource.removeEventListener("error", onError);

    eventSource.close();
  };
};
